import { motion } from "framer-motion";

const RECORD = [
  {
    year: "2025 — Present",
    company: "JumpSeat",
    role: "Senior Account Lead / Manager",
    place: "Los Angeles, CA",
    bullets: [
      "100% client retention through proactive relationship management.",
      "Led a ground-up brand overhaul of a century-old academic institution.",
      "Stood up the full content ecosystem: newsletters, site copy, social, voice guidelines.",
      "Positioned enterprise AI and tech clients with differentiated messaging and market narrative.",
    ],
    impact: "100% retention · Enterprise brand transformation · Full-stack content leadership",
  },
  {
    year: "2023 — 2025",
    company: "Muros",
    role: "Senior Marketing Manager",
    place: "Chicago, IL",
    bullets: [
      "Drove 550%+ marketing-attributed revenue growth.",
      "Built partnerships with every major U.S. professional sports league (NFL, NBA, MLB, NHL, MLS).",
      "Led brand activations at SXSW, Art Basel Miami, and Lollapalooza.",
      "Grew social 200% in two years through artist-led storytelling.",
      "Launched the company&apos;s first DEI programming initiatives.",
      "Created &ldquo;Behind the Brush,&rdquo; the artist-journey editorial series.",
    ],
    impact: "550%+ marketing revenue growth · League partnerships · Global activations",
  },
  {
    year: "2018 — 2022",
    company: "Revolution Global",
    role: "Marketing Manager",
    place: "Chicago, IL",
    bullets: [
      "Built Revolution into the #1 Cannabis Brand in 2021 and 2022 (High Times).",
      "Grew social following to 155,000+ — a 47,000% lift in under three years.",
      "Led marketing for 13 award-winning cultivars and products from 2020 – 2022.",
      "Forged cultural partnerships with Lyrical Lemonade, Riot Fest, and Jugrnaut.",
      "Owned creative direction, brand strategy, copywriting, and social strategy across the portfolio.",
    ],
    impact: "47,000% social growth · #1 industry brand · Award-winning portfolio",
  },
  {
    year: "2016 — 2018",
    company: "The Podyum",
    role: "Director of Marketing",
    place: "Sioux Falls, SD",
    bullets: [
      "Built marketing for international professional football training camps.",
      "Grew audience from zero to 155,000+ across four platforms.",
      "Interviewed 50+ professional players and coaches, producing narrative content from the conversations.",
    ],
    impact: "Global audience building · International sports marketing",
  },
  {
    year: "During College",
    company: "University of Iowa — Student Life",
    role: "Marketing Specialist",
    place: "Iowa City, IA",
    bullets: [
      "Ran marketing for internal U of Iowa clients and the HawkShop ecommerce platform.",
      "Grew social 130%, site traffic 45%, major-brand purchases 20% YoY.",
      "Built the brand identity and launch campaign for Snack Shack at IMU.",
    ],
    impact: "eCommerce growth · Campus brand development · Data-driven results",
  },
  {
    year: "2014 — 2018",
    company: "University of Iowa",
    role: "B.A. Journalism · B.A. Political Science · Tippie Entrepreneurial Mgmt.",
    place: "Iowa City, IA",
    bullets: ["Foundation in storytelling, strategy, and building from zero."],
    impact: "Where the discipline started.",
  },
];

export default function TrackRecord() {
  return (
    <section id="track-record" data-testid="track-record-section" className="px-4 md:px-8 lg:px-12 py-20 md:py-28">
      <div className="mb-12 md:mb-16 max-w-6xl">
        <div className="section-label mb-3 text-[#4A4A4A]">§ 04 — Track Record</div>
        <h2 className="font-anton uppercase text-5xl md:text-7xl lg:text-8xl leading-[0.92]">
          A decade of
          <span className="font-cormorant italic text-[#D83127] font-semibold"> measurable </span>
          outcomes.
        </h2>
        <p className="font-cormorant italic text-xl md:text-2xl mt-6 max-w-2xl text-[#1a1a1a]">
          Ten years of senior marketing work — the clients, the roles, and the results that
          defined each chapter.
        </p>
      </div>

      <ol className="relative">
        {RECORD.map((r, i) => (
          <motion.li
            key={r.company + r.year}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5 }}
            className="hard-border-t py-8 md:py-10 grid grid-cols-12 gap-4 md:gap-6"
            data-testid={`record-${i}`}
          >
            <div className="col-span-12 md:col-span-3">
              <div className="font-mono-s text-[11px] tracking-[0.25em] uppercase text-[#4A4A4A] mb-2">
                {r.year}
              </div>
              <div className="font-anton uppercase text-3xl md:text-4xl leading-tight">
                {r.company}
              </div>
              <div className="font-cormorant italic text-lg text-[#1a1a1a]">{r.role}</div>
              <div className="font-mono-s text-[10px] uppercase tracking-[0.2em] text-[#4A4A4A] mt-1">
                ⌖ {r.place}
              </div>
            </div>

            <ul className="col-span-12 md:col-span-6 space-y-2 font-work text-base md:text-lg leading-relaxed">
              {r.bullets.map((b, j) => (
                <li key={j} className="flex gap-3">
                  <span className="font-anton text-[#D83127]">▸</span>
                  <span dangerouslySetInnerHTML={{ __html: b }} />
                </li>
              ))}
            </ul>

            <div className="col-span-12 md:col-span-3">
              <div className="hard-border bg-[#F2EFE9] p-4">
                <div className="font-mono-s text-[10px] tracking-[0.3em] uppercase text-[#4A4A4A] mb-2">
                  Impact
                </div>
                <div className="font-cormorant italic text-lg leading-snug">{r.impact}</div>
              </div>
            </div>
          </motion.li>
        ))}
        <div className="hard-border-t" aria-hidden />
      </ol>
    </section>
  );
}
